import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Volume2, VolumeX, Sparkles } from 'lucide-react';
import { toggleSound, isSoundEnabled, playSoundIfEnabled } from '../utils/sounds';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSoundToggle = () => {
    const newState = toggleSound();
    setSoundOn(newState);
    if (newState) {
      playSoundIfEnabled('magic');
    }
  };

  const handleNavClick = () => {
    playSoundIfEnabled('click');
    setIsMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between py-4 lg:py-6">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            onClick={handleNavClick}
          >
            <Sparkles className="w-6 h-6 lg:w-8 lg:h-8 text-light-pink group-hover:text-mid-yellow transition-colors duration-300 group-hover:rotate-12 transform" />
            <div>
              <h1 className="text-lg lg:text-xl font-display font-bold text-light-pink group-hover:text-white transition-colors duration-300 leading-none">
                INSPIRE STUDIO
              </h1>
              <p className="text-xs lg:text-sm text-light-pink/70 font-mono uppercase tracking-wider">
                Oxford
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="nav-link text-light-pink hover:text-white transition-colors duration-200"
              onClick={handleNavClick}
            >
              Home
            </Link>
            <Link 
              to="/workshops" 
              className="nav-link text-light-pink hover:text-white transition-colors duration-200"
              onClick={handleNavClick}
            >
              Workshops
            </Link>
            <Link 
              to="/gallery" 
              className="nav-link text-light-pink hover:text-white transition-colors duration-200"
              onClick={handleNavClick}
            >
              Gallery
            </Link>
            <Link 
              to="/about" 
              className="nav-link text-light-pink hover:text-white transition-colors duration-200"
              onClick={handleNavClick}
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className="nav-link text-light-pink hover:text-white transition-colors duration-200"
              onClick={handleNavClick}
            >
              Contact
            </Link>
            
            {/* Sound Toggle */}
            <button
              onClick={handleSoundToggle}
              className="p-2 rounded-full bg-light-pink/10 hover:bg-light-pink/20 transition-all duration-200"
              aria-label="Toggle sound"
            >
              {soundOn ? (
                <Volume2 className="w-5 h-5 text-light-pink" />
              ) : (
                <VolumeX className="w-5 h-5 text-mid-grey" />
              )}
            </button>

            {/* CTA Button */}
            <Link 
              to="/workshops"
              className="cta-button bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300"
              onClick={handleNavClick}
            >
              Book Now
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 md:hidden">
            <button
              onClick={handleSoundToggle}
              className="p-2 rounded-full bg-light-pink/10"
              aria-label="Toggle sound"
            >
              {soundOn ? (
                <Volume2 className="w-5 h-5 text-light-pink" />
              ) : (
                <VolumeX className="w-5 h-5 text-mid-grey" />
              )}
            </button>
            <button
              onClick={() => {
                playSoundIfEnabled('click');
                setIsMenuOpen(!isMenuOpen);
              }}
              className="p-2 text-light-pink hover:text-white transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden pb-6 animate-accordion-down">
            <nav className="flex flex-col gap-4">
              <Link 
                to="/" 
                className="nav-link text-light-pink hover:text-white transition-colors py-2"
                onClick={handleNavClick}
              >
                Home
              </Link>
              <Link 
                to="/workshops" 
                className="nav-link text-light-pink hover:text-white transition-colors py-2"
                onClick={handleNavClick}
              >
                Workshops
              </Link>
              <Link 
                to="/gallery" 
                className="nav-link text-light-pink hover:text-white transition-colors py-2"
                onClick={handleNavClick}
              >
                Gallery
              </Link>
              <Link 
                to="/about" 
                className="nav-link text-light-pink hover:text-white transition-colors py-2"
                onClick={handleNavClick}
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className="nav-link text-light-pink hover:text-white transition-colors py-2"
                onClick={handleNavClick}
              >
                Contact
              </Link>
              <Link 
                to="/workshops"
                className="cta-button bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300 mt-2"
                onClick={handleNavClick}
              >
                Book Now
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
